/* Screen 1 - Policy Details (Plan Selection) */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { toast } from "sonner";
import tickicon from "../../../../assets/images/done.svg";
import listblackicon from "../../../../assets/images/list-black.svg";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import RenewalLayout from "../../components/RenewalLayout";
import PersonalCyberBenefitsModal from "../../../../components/newPersonalCyberPolicy/modals/personalCyberBenefitsModal";
import { policyPremium } from "../../../../redux/features/motor/renewalPremiumSlice";
import { getClientSource } from "../../../../utils/getClientSource";

// Plan options with all details for Personal Cyber Insurance
const CYBER_PLANS = [
  {
    id: "cyber_3300",
    option: "OPTION1",
    premium: 3.3,
    sumInsuredBHD: 189,
    sumInsuredUSD: 500,
    deductible: 5,
    features: [
      { amount: 189, currency: "BHD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 500, currency: "USD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 5, currency: "BHD", label: "Deductibles", label_ar: "مبلغ التحمل" },
    ],
  },
  {
    id: "cyber_4400",
    option: "OPTION2",
    premium: 4.4,
    sumInsuredBHD: 378,
    sumInsuredUSD: 1000,
    deductible: 5,
    features: [
      { amount: 378, currency: "BHD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 1000, currency: "USD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 5, currency: "BHD", label: "Deductibles", label_ar: "مبلغ التحمل" },
    ],
  },
  {
    id: "cyber_6600",
    option: "OPTION3",
    premium: 6.6,
    sumInsuredBHD: 756,
    sumInsuredUSD: 2000,
    deductible: 5,
    features: [
      { amount: 756, currency: "BHD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 2000, currency: "USD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 5, currency: "BHD", label: "Deductibles", label_ar: "مبلغ التحمل" },
    ],
  },
  {
    id: "cyber_8800",
    option: "OPTION4",
    premium: 8.8,
    sumInsuredBHD: 1323,
    sumInsuredUSD: 3500,
    deductible: 5,
    features: [
      { amount: 1323, currency: "BHD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 3500, currency: "USD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 5, currency: "BHD", label: "Deductibles", label_ar: "مبلغ التحمل" },
    ],
  },
  {
    id: "cyber_11000",
    option: "OPTION5",
    premium: 11.0,
    sumInsuredBHD: 1890,
    sumInsuredUSD: 5000,
    deductible: 5,
    features: [
      { amount: 1890, currency: "BHD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 5000, currency: "USD", label: "Sum-Insured", label_ar: "مبلغ التأمين" },
      { amount: 5, currency: "BHD", label: "Deductibles", label_ar: "مبلغ التحمل" },
    ],
  },
];

const PolicyDetailsPage = ({
  policyDetailsInfo,
  onContinue,
  onBack,
  formData,
  setFormData,
  premiumData,
  setPremiumData,
  premiumError,
  setPremiumError,
}) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [premiumLoading, setPremiumLoading] = useState(false);

  // Pre-select current plan from policyView on mount
  useEffect(() => {
    if (policyDetailsInfo && !formData.selectedPlan) {
      const currentPlanName = policyDetailsInfo?.PlanName;
      const currentSumInsured = policyDetailsInfo?.SumInsured;
      const currentPremium = policyDetailsInfo?.Premium;

      console.log("🔍 Matching Personal Cyber plan:", {
        currentPlanName,
        currentSumInsured,
        currentPremium,
      });

      // Try to find matching plan by PlanName first (e.g., "OPTION5")
      let matchedPlan = CYBER_PLANS.find(
        (plan) => plan.option === currentPlanName
      );

      // Fallback to matching by SumInsured
      if (!matchedPlan && currentSumInsured) {
        matchedPlan = CYBER_PLANS.find(
          (plan) => plan.sumInsuredBHD === Number(currentSumInsured)
        );
      }

      // Fallback to matching by Premium
      if (!matchedPlan && currentPremium) {
        matchedPlan = CYBER_PLANS.find(
          (plan) => plan.premium === Number(currentPremium)
        );
      }

      // Default to first plan if no match found
      if (!matchedPlan) {
        console.log("⚠️ No matching plan found, defaulting to first plan");
        matchedPlan = CYBER_PLANS[0];
      } else {
        console.log("✅ Matched plan:", matchedPlan.option);
      }

      setFormData((prev) => ({
        ...prev,
        selectedPlan: matchedPlan,
        originalPlan: matchedPlan,
      }));
    }
  }, [policyDetailsInfo, formData.selectedPlan, setFormData]);

  // Auto-fetch premium when selectedPlan is set
  useEffect(() => {
    if (formData.selectedPlan && !premiumData && !premiumLoading) {
      fetchPremiumForPlan(formData.selectedPlan);
    }
  }, [formData.selectedPlan]);

  // Fetch premium for selected plan (Real-time API call)
  const fetchPremiumForPlan = async (plan) => {
    setPremiumLoading(true);
    setPremiumError(null);

    try {
      const payload = {
        PolicyNumber: policyDetailsInfo?.PolicyNumber,
        Channel: getClientSource(),
      };

      // Always send NewPlan — the cyber premium endpoint requires it
      // to identify which plan to price, even when renewing the same plan.
      payload.NewPlan = plan.option;

      console.log("✅ Calling premium API for plan:", plan.option, payload);

      // Call premium API via Redux
      const premiumAction = await dispatch(policyPremium(payload));
      const premiumResponse = unwrapResult(premiumAction);

      setPremiumData(premiumResponse);
      console.log("✅ Premium calculated for plan:", plan.option, premiumResponse);

    } catch (error) {
      console.error("❌ Premium API error:", error);
      setPremiumError(error.message);
      toast.error(translate(
        "Failed to calculate premium. Please try again.",
        "فشل في حساب القسط. يرجى المحاولة مرة أخرى."
      ));
    } finally {
      setPremiumLoading(false);
    }
  };

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setFormData((prev) => ({
      ...prev,
      selectedPlan: plan,
    }));

    // Immediately fetch premium for selected plan (REAL-TIME)
    fetchPremiumForPlan(plan);
  };

  // Handle continue button
  const handleContinue = () => {
    if (formData.selectedPlan) {
      onContinue();
    }
  };

  // Handle view benefits modal
  const handleViewBenefits = () => {
    setShowBenefitsModal(true);
  };

  // Format feature for display
  const formatFeature = (feature) => {
    const currencySymbol = feature.currency === "USD" ? "USD" : "BD";
    const formattedAmount =
      feature.currency === "USD"
        ? new Intl.NumberFormat("en-US").format(feature.amount)
        : feature.amount.toLocaleString("en-US");

    return translate(
      `${currencySymbol} ${formattedAmount} ${feature.label}`,
      `${feature.label_ar} ${formattedAmount} ${feature.currency === "USD" ? "دولار أمريكي" : "د.ب"}`
    );
  };

  // Format premium for display
  const formatPremium = (premium) => {
    return translate(
      `BHD ${premium.toFixed(3)}`,
      `${premium.toFixed(3)} د.ب`
    );
  };

  // Use pricing from premium API if available, fallback to policyDetailsInfo
  const basePremium = premiumData?.Premium || policyDetailsInfo?.Premium || 0;
  const VAT = premiumData?.Tax || policyDetailsInfo?.Tax || 0;
  const totalPrice = premiumData?.TotalPremium || policyDetailsInfo?.TotalPremium || (basePremium + VAT);

  // Format plans for the benefits modal
  const formattedPlansForModal = CYBER_PLANS.map((plan) => ({
    ...plan,
    name: `BHD ${plan.premium.toFixed(3)}`,
    name_ar: `${plan.premium.toFixed(3)} د.ب`,
    features: plan.features.map((feature) => ({
      en: formatFeature(feature),
      ar: formatFeature(feature),
    })),
  }));

  return (
    <div className="mainCarDetailsModal pt-[1%] pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[2%] ipad:h-[93vh] ipad:overflow-y-scroll">
      <RenewalLayout
        policyType="personalCyber"
        show_close_btn={false}
        is_hide_back_btn={false}
        backClickHandler={onBack}
        is_bottom_sticky_layout={true}
        show_summary={premiumData && !premiumError}
        btn_label={translate("Continue", "التالي")}
        btn_onClick={handleContinue}
        btn_disabled={!formData.selectedPlan || premiumLoading || !!premiumError}
        tempPrice={premiumData && !premiumError ? totalPrice : undefined}
      >
        <div className="flex flex-col items-center p-6 max767:p-0 mx-auto pt-0 py-5 w-full">
          <div className="max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0 w-full max-w-4xl">
            {/* Header */}
            <div className="text-center mb-8">
              <TitleDescription
                title={translate("Policy Details", "تفاصيل السياسة")}
                description={translate(
                  "Choose whether to keep or update your policy details.",
                  "يرجى اختيار سقف السياسة من القائمة أدناه."
                )}
              />
              <p className="text-[#1A2024] text-center text-base not-italic font-normal leading-6 tracking-[-0.16px] mt-4 max767:text-left max767:rtl:text-right rtl:hidden">
                {translate(
                  "Please select a sum insured limit from the list below.",
                  "يرجى اختيار سقف السياسة من القائمة أدناه."
                )}
              </p>
            </div>

            {/* Sum Insured Selection Cards - Flex layout with centered alignment */}
            <div className="flex flex-wrap justify-center gap-3 mb-8 max767:flex-col max767:items-center ipad:gap-4 w-full max-w-[900px] mx-auto">
              {CYBER_PLANS.map((plan, index) => {
                const isSelected = formData.selectedPlan?.id === plan.id;

                return (
                  <div key={`wrapper-${plan.id}`} className="contents">
                    <div
                      className={`flex relative w-[280px] min-w-[280px] h-auto flex-col p-5 pb-4 rounded-[10px] border-solid box-border max767:w-full max767:min-w-full transition-all duration-300 cursor-pointer hover:-translate-y-0.5 hover:shadow-lg ${
                        isSelected
                          ? "border-2 border-[#00719A] bg-[#E3F8FF]"
                          : "border border-[#DDE2E4] bg-white"
                      }`}
                      onClick={() => handlePlanSelect(plan)}
                    >
                      {/* Radio button indicator */}
                      <div className="absolute top-4 right-4 rtl:right-auto rtl:left-4">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected
                              ? "border-[#00A9E7] bg-[#00A9E7]"
                              : "border-[#DDE2E4] bg-[#F6F8F9]"
                          }`}
                        >
                          {isSelected && (
                            <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                          )}
                        </div>
                      </div>

                      {/* Plan price as header */}
                      <div className="w-full">
                        <h3 className="text-[#1A2024] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] text-left rtl:text-right rtl:flex-row-reverse mb-3">
                          {formatPremium(plan.premium)}
                        </h3>
                      </div>

                      {/* Features with checkmarks */}
                      <div className="flex flex-col gap-1 w-full mb-1">
                        {plan.features.map((feature, index) => (
                          <div
                            key={index}
                            className="feature flex gap-2 items-start"
                          >
                            <img
                              src={tickicon}
                              alt="tick icon"
                              className="mt-0.5 flex-shrink-0 w-3 h-3"
                            />
                            <p className="text-black text-xs not-italic font-normal leading-[150%] text-left rtl:text-right">
                              {formatFeature(feature)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* View Benefits Button */}
            <div className="flex justify-center mb-6">
              <button
                className="flex items-center gap-2 border bg-white px-4 py-2 rounded-[10px] border-solid border-[#DDE2E4] text-[#1A2024] text-sm not-italic font-semibold leading-6 tracking-[-0.084px]"
                onClick={handleViewBenefits}
              >
                <img src={listblackicon} alt="list icon" />
                {translate(
                  "View full list of benefits & covers",
                  "عرض القائمة الكاملة للمزايا والتغطيات"
                )}
              </button>
            </div>
          </div>
        </div>
      </RenewalLayout>

      {/* Personal Cyber Benefits Modal */}
      <PersonalCyberBenefitsModal
        show={showBenefitsModal}
        onClose={() => setShowBenefitsModal(false)}
        plans={formattedPlansForModal}
      />
    </div>
  );
};

export default PolicyDetailsPage;
